import { inject, Injectable } from '@angular/core'
import { finalize, Observable, Subject, take } from 'rxjs'
import { AlbertModalService } from './albert-modal.service'

export type UnsavedChangesDecision = 'save' | 'discard' | 'cancel'

const UNSAVED_CHANGES_TITLE = 'Unsaved Changes'
const UNSAVED_CHANGES_MESSAGE = 'Do you want to save before leaving the page or cancel all changes?'

@Injectable({
  providedIn: 'root'
})
export class ModalHelperService {
  private readonly modalService = inject(AlbertModalService)

  /**
   * Shows a confirmation dialog when leaving a page with unsaved changes. It opens in the prompt slot, above content
   * modals and below the technical error of a failed save, and stays open to retry, cancel or close.
   * @param saveCallback A function that returns an Observable that performs the save operation. Completing without a
   * value counts as a failed save that was already reported.
   * @param errorCallback An optional callback to report errors during the save operation.
   * @returns An Observable that emits true if the user chooses to leave (either by saving successfully or choosing 'Cancel'),
   * or false if the user chooses to stay by clicking the X.
   */
  public confirmLeave(saveCallback: () => Observable<unknown>, errorCallback?: (error: unknown) => void): Observable<boolean> {
    const decision$ = new Subject<boolean>()
    // Ignores further clicks on Confirm while the save is running, so it is not sent twice.
    let saving = false
    this.modalService.showPrompt({
      title: UNSAVED_CHANGES_TITLE,
      message: UNSAVED_CHANGES_MESSAGE,
      type: 'warning',
      closable: true,
      onClose: () => decision$.next(false),
      actions: [
        { label: 'Cancel', position: 'right', severity: 'secondary', outlined: true, callback: () => decision$.next(true) },
        {
          label: 'Confirm',
          position: 'right',
          severity: 'primary',
          callback: () => {
            if (saving) return
            saving = true
            saveCallback().pipe(
              take(1),
              finalize(() => saving = false)
            ).subscribe({
              next: () => decision$.next(true),
              error: (error) => {
                if (errorCallback) errorCallback(error)
              }
            })
          }
        }
      ]
    })
    return decision$.pipe(take(1), finalize(() => this.modalService.hidePrompt()))
  }

  /**
   * Asks whether to save unsaved changes. Unlike {@link confirmLeave}, it does not save: the caller acts on the
   * decision, e.g. stays in the content modal the changes were made in, which the dialog opens above.
   * @returns An Observable that emits the user's decision once: 'save' on Confirm, 'discard' on Cancel, 'cancel' on the X.
   */
  public confirmUnsavedChanges(): Observable<UnsavedChangesDecision> {
    const decision$ = new Subject<UnsavedChangesDecision>()
    this.modalService.show({
      title: UNSAVED_CHANGES_TITLE,
      message: UNSAVED_CHANGES_MESSAGE,
      type: 'warning',
      closable: true,
      onClose: () => decision$.next('cancel'),
      actions: [
        { label: 'Cancel', position: 'right', severity: 'secondary', outlined: true, callback: () => decision$.next('discard') },
        { label: 'Confirm', position: 'right', severity: 'primary', callback: () => decision$.next('save') }
      ]
    })
    return decision$.pipe(take(1), finalize(() => this.modalService.hide()))
  }

  /**
   * Shows a generic delete confirmation dialog.
   * @param title The title of the dialog.
   * @param message The message of the dialog.
   * @returns An Observable that emits true if the user confirms deletion, or false otherwise.
   */
  public confirmDelete(title: string, message: string): Observable<boolean> {
    const decision$ = new Subject<boolean>()
    this.modalService.show({
      title,
      message,
      type: 'warning',
      actions: [
        { label: 'No', position: 'left', styleClass: 'p-button-text btn-secondary', callback: () => decision$.next(false) },
        { label: 'Yes', position: 'right', styleClass: 'p-button-danger btn-secondary', callback: () => decision$.next(true) },
      ]
    })
    return decision$.pipe(take(1), finalize(() => this.modalService.hide()))
  }
}
